import React, {createRef} from "react"
import {findDOMNode} from "react-dom"

import Breadcrumbs from '../../../../core/gd-components/breadcrumbs/breadcrumbs'
import FinderTask from '../items/task/finder-task'
import FinderProject from '../items/project/finder-project'
import FinderNavigateUp from '../items/navigate-up/finder-navigate-up'
import VScroll from "../../scrolling/custom-v-scroll/v-scroll"
import FinderTasksCollection from "../../../model2/task/finder/collection"
import FinderTaskModel from "../../../model2/task/finder/finder-task-model"
import {SpaceProto} from "../../../model2/space/space-proto"
import Workspace from "../../../model2/space/workspaces/workspace"

type Props = {
    companyId?      : ID
    selectedTaskId? : ID
    parentProjectId?: ID | null

    search?         : string
    data            : FinderTasksCollection | null

    isSearching?    : boolean
    noSubtasks?     : boolean
    showRecent?     : boolean

    // Off by default -- Home is otherwise hidden at the root because there is
    // no project to name there. Only the entity selector's Task Browse wants
    // it named "Home" instead; every other host keeps hiding the row there.
    alwaysShowHome? : boolean

    onClickCompany? : func
    onClickProject? : func
    onClickTask?    : func
    onTab?          : {():void}
}

type State = {
    currentCursorKey: any
}

// const debug = "TaskFinderMain"
const debug = false

export default class TaskFinderMain extends React.Component<Props, State> {

    private scrollRef: any          = createRef()
    private selectedItemRef: any    = createRef()

    private optionsLength: number   = 0

    // Enter drives the very same handlers the mouse does -- it calls the
    // highlighted row's own `click()` (see `onKeyDown`) -- so a handler cannot
    // tell the two apart by what it is given. This is how it tells: set around
    // that call, read and cleared by `onClickProject`, which runs inside it.
    private isKeyboardNavigation: boolean = false

    constructor(props: Props) {
        super(props)

        debug && console.log(debug, 'constructor', this.props.data)

        this.state = {
            currentCursorKey: null
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State, snapshot: any) {
        debug && console.log(debug, 'componentDidUpdate', this.state.currentCursorKey)

        if (this.scrollRef.current) {
            const node: any = findDOMNode(this.selectedItemRef.current)
            if (node) {
                this.scrollRef.current?.setScrollTop(node.offsetTop + node.clientHeight/2,true,250)
            }
        }
    }

    // Opening a project by MOUSE leaves no cursor behind. The highlight is the
    // keyboard's own, and lighting up the first row of the level just opened
    // reads as a selection the user never made -- one they then have to arrow
    // away from. Enter keeps it, landing on that first row so the arrows can
    // carry straight on down the new level.
    //
    // Index 1, not 0: entering a project always lands somewhere non-root, so
    // the level just opened always renders its own ".." row at index 0 first
    // (see `renderItems()`) -- 1 is the first real project/task under it.
    //
    // Going UP is not this: `onNavigateUp` sets a cursor deliberately, on the
    // project being left, so ArrowLeft/ArrowRight can retrace the path.
    onClickProject = (id: ID) => {
        this.setState({
            currentCursorKey: this.isKeyboardNavigation ? 1 : null
        }, ()=>{
            this.props.onClickProject?.(id)
        })
    }

    // The breadcrumb's own "company" step -- a mouse click like any other, so it
    // drops the cursor rather than carrying an index into a level where it means
    // something else. (`onNavigateUp` reaches the same place by keyboard and
    // brings its own.)
    onClickCompany = () => {
        this.setState({
            currentCursorKey: null
        }, ()=>{
            this.props.onClickCompany?.()
        })
    }

    onClickTask = (itemKey: number, task: FinderTaskModel) => {
        this.setState({
            currentCursorKey: itemKey
        }, ()=>{
            this.props.onClickTask?.(task)
        })
    }

    // `parentProjectId` walks DOWN a level at a time (a project's OWN id, or
    // the 'projects' pseudo-space, or null for the space list), so going back
    // up has to unwind that same chain rather than just reading `parentId` --
    // a project sitting at the top of a tree has a null `parentId` whether it
    // is a workspace root (parent: the space list) or a "Projects" space root
    // (parent: the 'projects' pseudo-level), and only `.isWorkspace` tells the
    // two apart.
    onNavigateUp = () => {
        const parentProjectId = this.props.parentProjectId
        if (!parentProjectId) return // already at the space list -- nothing above it

        let target: ID | null

        if (parentProjectId === 'projects') {
            target = null
        } else {
            const project = gd.session.projects.get(parentProjectId)
            if (!project) return

            if (project.parentId) {
                target = project.parentId
            } else {
                target = project.isWorkspace ? null : 'projects'
            }
        }

        // The project we're leaving is one of the rows about to be rendered at
        // `target` -- land the cursor on it instead of dropping the highlight,
        // so ArrowLeft/ArrowRight can retrace the same path back down. Offset
        // by 1 when `target` itself isn't root: that level renders its own
        // ".." row first (see `renderItems()`), shifting every sibling down.
        const siblingIndex = this.getSiblingIndex(target, parentProjectId)
        const currentCursorKey = siblingIndex === null ? null : siblingIndex + (target ? 1 : 0)

        this.setState({
            currentCursorKey
        }, ()=>{
            if (target === null) {
                this.props.onClickCompany?.()
            } else {
                this.props.onClickProject?.(target)
            }
        })
    }

    // The index `childId` will render at within `levelParentId`'s listing --
    // mirrors the project ordering in `renderItems()` (root: `spacesWithProjects`
    // in its own order, everywhere else: children sorted by name) so the two
    // never drift apart.
    getSiblingIndex(levelParentId: ID | null, childId: ID): number | null {
        let index

        if (levelParentId === null) {
            const spaces = _.filter(gd.spaces.spacesWithProjects, (space: SpaceProto)=>space.enabled)

            index = _.findIndex(spaces, (space: SpaceProto)=>{
                return space instanceof Workspace ? space.project.id === childId : childId === 'projects'
            })
        } else {
            const projects = levelParentId === 'projects'
                ? gd.spaces.projects.rootProjects
                : gd.session.projects.get(levelParentId)?.treeChildren

            const filteredProjects = _.orderBy(
                _.filter(projects, p=>!p.isTag && !p.isView && !p.isLink),
                p=>p.name.toLowerCase()
            )

            index = _.findIndex(filteredProjects, p=>p.id === childId)
        }

        return index >= 0 ? index : null
    }

    onKeyDown = (event: any) => {
        debug && console.log(debug, 'onKeyDown', this.state.currentCursorKey, event)

        let newValue
        switch(event.code){
            case 'Tab':
                event.preventDefault()
                this.props.onTab && this.props.onTab()
                break

            case 'ArrowUp':
                if (_.isNil(this.state.currentCursorKey)) {
                    newValue = 0
                } else {
                    newValue = this.state.currentCursorKey - 1
                    if (newValue < 0) newValue = 0
                }

                this.setState({
                    currentCursorKey: newValue
                })
                break

            case 'ArrowDown':
                if (_.isNil(this.state.currentCursorKey)) {
                    newValue = 0
                } else {
                    newValue = this.state.currentCursorKey + 1
                    if (newValue > this.optionsLength - 1) newValue = this.optionsLength - 1;
                }

                this.setState({
                    currentCursorKey: newValue
                })
                break

            case 'ArrowLeft':
                // Only outside a search: the field still owns ArrowLeft while
                // there is text in it, or the caret could never move left
                // through a query to edit it.
                if (!this.props.search) {
                    event.preventDefault()
                    this.onNavigateUp()
                }
                break

            case 'Enter':
                event.preventDefault()
                event.stopPropagation()

                if (this.selectedItemRef.current) {
                    // Cleared straight after: `click()` runs the handler
                    // synchronously, so the flag is only ever up for this call.
                    this.isKeyboardNavigation = true
                    this.selectedItemRef.current.click()
                    this.isKeyboardNavigation = false
                }
                break
        }
    }

    renderItems() {
        if (this.props.isSearching) {
            return <div className="searching-label">Searching tasks...</div>
        } else {
            let res: any[] = [], filteredProjects = [], baseKey = 0
            let projects

            // Only away from root, and only while actually browsing the tree
            // (search/recent show a flat task list -- there is no level to
            // step out of there).
            const showNavigateUp = !this.props.search && !this.props.showRecent && !!this.props.parentProjectId
            const extraKey = showNavigateUp ? 1 : 0

            switch (this.props.parentProjectId) {
                case null:
                    // Recent shows a flat task list -- no space/project rows.
                    if (
                        !this.props.search
                        && !this.props.showRecent
                    ) {
                        const spaces = _.filter(gd.spaces.spacesWithProjects, (space: SpaceProto) => {
                            return space.enabled
                        })

                        _.map(spaces, (space: SpaceProto, key: number)=>{
                            const isSelected = key === this.state.currentCursorKey

                            if (space instanceof Workspace) {
                                res.push(
                                    <FinderProject
                                        ref             = {isSelected ? this.selectedItemRef : null}
                                        key             = {space.id}
                                        isHighlighted   = {isSelected}
                                        project         = {space.project}
                                        onClickProject  = {this.onClickProject}
                                    />
                                )
                            } else {
                                res.push(
                                    <FinderProject
                                        ref             = {isSelected ? this.selectedItemRef : null}
                                        key             = {gd.spaces.projects.id}
                                        isHighlighted   = {isSelected}
                                        project         = {gd.spaces.projects.project}
                                        onClickProject  = {()=>{this.onClickProject?.('projects')}}
                                    />
                                )
                            }
                        })
                        baseKey = filteredProjects.length
                    }
                    break

                case 'projects':
                    projects = _.filter(gd.spaces.projects.rootProjects, p=>{
                        return !p.isTag && !p.isView && !p.isLink
                    })
                    break

                default:
                    projects = _.filter(gd.session.projects.get(this.props.parentProjectId)?.treeChildren, p=>{
                        return !p.isTag && !p.isView && !p.isLink
                    })
            }

            if (
                !this.props.search
                && !this.props.showRecent
            ) {
                filteredProjects    = _.orderBy(projects, p=>{
                    return p.name.toLowerCase()
                })

                if (showNavigateUp) {
                    const isSelected = 0 === this.state.currentCursorKey
                    res.push(
                        <FinderNavigateUp
                            ref             = {isSelected ? this.selectedItemRef : null}
                            key             = "navigate-up"
                            isHighlighted   = {isSelected}
                            onNavigateUp    = {this.onNavigateUp}
                        />
                    )
                }

                _.map(filteredProjects, (p,key)=>{
                    const itemKey = key + extraKey
                    const isSelected = itemKey === this.state.currentCursorKey
                    res.push(
                        <FinderProject
                            ref             = {isSelected ? this.selectedItemRef : null}
                            key             = {p.id}
                            isHighlighted   = {isSelected}
                            project         = {p}
                            onClickProject  = {this.onClickProject}
                        />
                    )
                })
                baseKey = filteredProjects.length + extraKey
            }

            if (this.props.data) {
                const filteredTasks = _.orderBy(this.props.data.rootModels, (task: FinderTaskModel)=>task.sortPosition)
                _.map(filteredTasks, (task,key)=>{
                    const itemKey = baseKey + key
                    const isSelected = itemKey === this.state.currentCursorKey
                    res.push(
                        <FinderTask
                            ref                 = {isSelected ? this.selectedItemRef : null}
                            key                 = {task.id}
                            isHighlighted       = {isSelected}
                            task                = {task}
                            noSubtasks          = {this.props.noSubtasks}
                            onClickTask         = {this.onClickTask.bind(this, itemKey)}
                            showParentProject   = {!!this.props.search}
                            selected            = {this.props.selectedTaskId === task.id}
                        />
                    )
                })
            }

            const tasksLength = this.props.data ? this.props.data.length : 0
            if (!this.props.search && !this.props.showRecent) {
                this.optionsLength = filteredProjects.length + tasksLength + extraKey
            } else {
                this.optionsLength = tasksLength
            }

            return <div className="search-items">{res}</div>
        }
    }

    renderNotification() {
        const projects      = _.filter(gd.session.projects.treeRoots, p=>p.parentId === this.props.parentProjectId);
        const showNoTasks   = projects.length <= 0 && !!this.props.parentProjectId && !!this.props.data && this.props.data.length === 0 && !this.props.search;
        const noMatches     = !!this.props.data && this.props.data.length === 0 && !!this.props.search && !this.props.isSearching;
        const showLoading   = !this.props.data && !this.props.search && !this.props.isSearching;

        let label = null;
        if (showNoTasks) label = <div className="not-found">No tasks found</div>;
        else if (noMatches) label = <div className="not-found">No tasks found</div>;
        else if (showLoading) label = <div className="not-found">Loading...</div>;

        return label;
    }

    render() {
        // No row at all at the root -- there is no project to name -- unless
        // `alwaysShowHome` opts in, in which case `Breadcrumbs` renders Home
        // by itself once there is no project underneath it.
        const showBreadcrumbs = !this.props.search && !this.props.showRecent
            && (!!this.props.parentProjectId || this.props.alwaysShowHome)

        const mainClasses = Classes("finder-main-container", {
            'with-breadcrumbs': showBreadcrumbs
        })

        // The breadcrumb row's inset lives in finder.scss rather than in helper
        // classes on the element: a host that restyles the row -- the entity
        // selector turns it into a full-bleed band -- cannot override their
        // `!important`.
        return (
            <div ref="main" className="finder-main">
                {showBreadcrumbs &&
                    <div className="finder-breadcrumbs-row flex-column-left-top">
                        {!this.props.showRecent &&
                            <Breadcrumbs
                                className                       = "finder-breadcrumbs"
                                companyId                       = {this.props.companyId}
                                projectId                       = {null}
                                currentProject                  = {this.props.parentProjectId}
                                currentProjectAsLastBreadcrumb  = {true}
                                showHomeAtRoot                  = {this.props.alwaysShowHome}
                                onNavigateCompany               = {this.onClickCompany}
                                onNavigateProject               = {this.onClickProject}
                            />
                        }
                        <div className="horizontal-separator"/>
                    </div>
                }

                <VScroll
                    ref             = {this.scrollRef}
                    containerClass  = {mainClasses}
                    minDragHeight   = {20}
                >
                    {this.renderItems()}
                    {this.renderNotification()}
                </VScroll>
            </div>
        )
    }
}